import { mix, shade, TYPE_COL, UI, withA, type Col } from "../art/pal.ts";
import { TYPE_META, type TypeId } from "../data/types.ts";
import { measureText, wrapPx } from "../engine/font.ts";
import type { Gfx } from "../engine/gfx.ts";

/**
 * The 2026 interface kit: rounded glass panels with a lit top rim and soft drop shadow, pill
 * badges, gradient meters with a drain ghost, bouncing cursors and a named dialogue box.
 */

export function glass(g: Gfx, x: number, y: number, w: number, h: number, o: { r?: number; alpha?: number; shadow?: boolean; tone?: Col; border?: Col } = {}): void {
  const r = o.r ?? 5;
  const a = o.alpha ?? 236;
  if (o.shadow !== false) g.rrect(x + 1, y + 3, w, h, r, [4, 5, 12, 90]);
  g.rrect(x, y, w, h, r, withA(o.border ?? UI.line, 255));
  const top = o.tone ?? UI.bg2;
  g.gradV(x + 1, y + 1, w - 2, h - 2, withA(top, a), withA(mix(top, UI.bg0, 0.75), a), Math.max(0, r - 1));
  g.hline(x + r, y + 1, w - 2 * r, withA(UI.lineHi, 120));
}

/** Light paper card (for readable long text on bright scenes). */
export function paper(g: Gfx, x: number, y: number, w: number, h: number, r = 5): void {
  g.rrect(x + 1, y + 3, w, h, r, [4, 5, 12, 80]);
  g.rrect(x, y, w, h, r, UI.paperInk);
  g.gradV(x + 1, y + 1, w - 2, h - 2, UI.paper, UI.paperDk, r - 1);
  g.hline(x + r, y + 1, w - 2 * r, UI.white);
}

export function pill(g: Gfx, x: number, y: number, w: number, h: number, fill: Col, rim?: Col): void {
  const r = h >> 1;
  if (rim) g.rrect(x - 1, y - 1, w + 2, h + 2, r + 1, rim);
  g.rrect(x, y, w, h, r, fill);
}

/** Type badge; returns its width. */
export function typeBadge(g: Gfx, x: number, y: number, type: TypeId, compact = false): number {
  const meta = TYPE_META[type];
  const c = TYPE_COL[type]!;
  const label = compact ? meta.chip : meta.name;
  const w = measureText(label) + 8;
  g.rrect(x, y, w, 11, 4, c.rim);
  g.gradV(x + 1, y + 1, w - 2, 9, c.hi, c.fill, 3);
  g.hline(x + 3, y + 1, w - 6, withA(UI.white, 150));
  // Pale chips (MOON gold, HODL ice) take dark ink; white on them washed out to a blank tag.
  const pale = c.fill[0] * 0.299 + c.fill[1] * 0.587 + c.fill[2] * 0.114 > 190;
  if (pale) g.textShadow(x + 4, y + 2, label, shade(c.rim, 0.7), c.hi);
  else g.textShadow(x + 4, y + 2, label, UI.white, shade(c.rim, 0.55));
  return w;
}

export function hpTone(t: number): { fill: Col; hi: Col; lo: Col } {
  if (t > 0.5) return { fill: UI.green, hi: [170, 255, 180, 255], lo: UI.greenDk };
  if (t > 0.2) return { fill: UI.yellow, hi: [255, 245, 170, 255], lo: UI.yellowDk };
  return { fill: UI.red, hi: [255, 170, 160, 255], lo: UI.redDk };
}

/** Rounded meter with trough, drain ghost and glossy lid. */
export function meter(g: Gfx, x: number, y: number, w: number, h: number, t: number, o: { ghost?: number; tone?: { fill: Col; hi: Col; lo: Col }; trough?: Col } = {}): void {
  const r = h >> 1;
  g.rrect(x, y, w, h, r, UI.ink);
  g.rrect(x + 1, y + 1, w - 2, h - 2, Math.max(0, r - 1), o.trough ?? [40, 44, 72, 255]);
  const inner = w - 2;
  const span = (v: number) => Math.round(inner * Math.max(0, Math.min(1, v)));
  if (o.ghost !== undefined && o.ghost > t) {
    const gw = span(o.ghost);
    if (gw > 0) g.rrect(x + 1, y + 1, gw, h - 2, Math.max(0, r - 1), [255, 240, 230, 220]);
  }
  const tone = o.tone ?? hpTone(t);
  const fw = span(t);
  if (fw > 0) {
    g.gradV(x + 1, y + 1, fw, h - 2, tone.hi, tone.lo, Math.min(Math.max(0, r - 1), fw >> 1));
    if (h >= 5) g.hline(x + 2, y + 1, Math.max(0, fw - 2), withA(UI.white, 110));
  }
}

/** Bouncing ▶ cursor. */
export function cursor(g: Gfx, x: number, y: number, now: number, reduced: boolean, c: Col = UI.gold): void {
  const dx = reduced ? 0 : Math.round(Math.sin(now / 130) * 1.5);
  g.textShadow(x + dx, y, "▶", c, UI.ink);
}

/** Focus ring with a travelling shine. */
export function focus(g: Gfx, x: number, y: number, w: number, h: number, now: number, reduced: boolean, r = 4): void {
  g.rrect(x, y, w, h, r, withA(UI.gold, 60));
  g.rrectLine(x, y, w, h, r, UI.gold);
  if (!reduced) {
    const p = (now / 900) % 1.6;
    const sx = Math.round(x + (w + 20) * p - 10);
    for (let k = 0; k < 5; k++) {
      const xx = sx + k;
      if (xx > x + r && xx < x + w - r) {
        g.p(xx, y, UI.goldHi);
        g.p(xx - 2, y + h - 1, withA(UI.goldHi, 160));
      }
    }
  }
}

/** Round button glyph (A / B / etc.). */
export function keyCap(g: Gfx, x: number, y: number, key: string, tone: "a" | "b" | "n" = "n"): number {
  const fill = tone === "a" ? UI.pink : tone === "b" ? [79, 140, 255, 255] as Col : UI.bg3;
  const w = Math.max(11, measureText(key) + 6);
  g.rrect(x, y + 1, w, 11, 5, UI.ink);
  g.rrect(x, y, w, 11, 5, shade(fill, 0.7));
  g.rrect(x, y, w, 10, 5, fill);
  g.hline(x + 3, y + 1, w - 6, withA(UI.white, 120));
  g.text(x + Math.round((w - measureText(key)) / 2), y + 2, key, UI.white);
  return w;
}

/** Key cap + label. Returns total width. */
export function hint(g: Gfx, x: number, y: number, key: string, label: string, tone: "a" | "b" | "n" = "n", c: Col = UI.text): number {
  const kw = keyCap(g, x, y, key, tone);
  g.textShadow(x + kw + 3, y + 2, label, c, UI.ink);
  return kw + 3 + measureText(label);
}

export function coin(g: Gfx, x: number, y: number, spin = 0): void {
  const w = Math.max(1, Math.round(4 * Math.abs(Math.cos(spin))));
  g.ellipse(x + 4.5, y + 4.5, w + 0.5, 4.5, [120, 70, 20, 255]);
  g.ellipse(x + 4.5, y + 4, w, 4, UI.gold);
  g.ellipse(x + 4.5, y + 4, Math.max(0.5, w - 1.5), 2.5, UI.goldHi);
  if (w > 2) g.vline(x + 4, y + 2, 4, UI.goldDk);
}

/** Named dialogue box. `shown` is the typed-so-far text. */
export function dialog(g: Gfx, text: string, o: { name?: string; more?: boolean; now: number; reduced: boolean; y?: number; h?: number; x?: number; w?: number; age?: number }): void {
  const x = o.x ?? 12, w = o.w ?? 360;
  const h = o.h ?? Math.max(34, Math.min(4, wrapPx(text, w - 24).length) * 12 + 16);
  const pop = o.age === undefined || o.reduced ? 1 : Math.min(1, o.age / 140);
  const y = (o.y ?? 216 - h - 8) + Math.round((1 - pop) * 10);
  glass(g, x, y, w, h, { r: 6 });
  if (o.name) {
    const nw = measureText(o.name) + 14;
    g.rrect(x + 10, y - 7, nw, 13, 5, UI.ink);
    g.gradV(x + 11, y - 6, nw - 2, 11, UI.goldHi, UI.gold, 4);
    g.text(x + 17, y - 4, o.name, UI.paperInk);
  }
  const lines = wrapPx(text, w - 24).slice(0, Math.max(1, Math.floor((h - 12) / 11)));
  lines.forEach((line, i) => g.textShadow(x + 12, y + 9 + i * 12, line, UI.text, UI.ink));
  if (o.more) {
    const b = o.reduced ? 0 : Math.round(Math.abs(Math.sin(o.now / 180)) * 2);
    g.text(x + w - 16, y + h - 12 + b, "▼", UI.gold);
  }
}

/** Screen title bar with an accent stripe. */
export function titleBar(g: Gfx, label: string, right?: string, accent: Col = UI.teal): void {
  g.gradV(0, 0, 384, 22, withA(UI.bg2, 250), withA(UI.bg1, 250));
  g.hline(0, 22, 384, UI.ink);
  g.hline(0, 21, 384, withA(accent, 180));
  g.rrect(8, 6, 4, 10, 2, accent);
  g.textShadow(18, 8, label, UI.text, UI.ink);
  if (right) {
    const w = measureText(right);
    g.textShadow(376 - w, 8, right, UI.textDim, UI.ink);
  }
}

/** Animated gradient backdrop for menu screens. */
export function backdrop(g: Gfx, now: number, reduced: boolean, a: Col = [26, 32, 64, 255], b: Col = [12, 14, 30, 255]): void {
  g.gradV(0, 0, 384, 216, a, b);
  // Soft diagonal light bands.
  const t = reduced ? 0 : now / 60;
  for (let i = -2; i < 8; i++) {
    const x0 = Math.round(((i * 70 + t) % 560) - 120);
    for (let y = 0; y < 216; y += 1) {
      const xx = x0 + Math.round(y * 0.45);
      g.fill(xx, y, 26, 1, [255, 255, 255, 6]);
    }
  }
  // Dot grid.
  for (let y = 6; y < 216; y += 12) for (let x = 6 + ((y / 12) % 2) * 6; x < 384; x += 12) g.p(x, y, [255, 255, 255, 14]);
}
